<script setup>
import {
  hasRegFlowDeepLinkPending,
  normalizeAvailChartsLockRoute,
  syncLocationAvChartDataPage,
  tightenRegFlowChartHashInLocationBar,
  scrollToLocationAvChart,
} from '~/utils/location-av-chart-deeplink'

const props = defineProps({
  location: Object,
  showPromoBanner: Boolean,
  dataPage: {
    type: String,
    required: true,
  },
})

// defineExpose must appear before any top-level `await` (vue/no-expose-after-await).
defineExpose({
  scrollToChart: () => scrollToLocationAvChart({ showPromoBanner: props.showPromoBanner }),
})

const { version } = useZigZag()
const config = useRuntimeConfig()
const { data, refresh: _refresh, error: _error } = await version(config.public.zigzagUrl)

const time = data.value ? data.value.t : 0;
const sha = data.value ? data.value.sha : 'versionnotfound';

const zigzagUrl = `${config.public.zigzagUrl}/app/layout/shell.html?sha=${sha}&t=${time}`;
const { prefetchPulseRoute } = usePulsePrefetch()
const chartContainerRef = ref(null)
const { showPulseChartError, startPulseChartMonitoring } = usePulseChartFallback()
let dataPageObserver = null

const applyChartDataPage = (refreshPulse = true) => {
  dataPageObserver?.disconnect()
  syncLocationAvChartDataPage(props.dataPage, { refreshPulse })
  // Skip cosmetic hash tightening while a deep link is pending — replaceState would convert
  // Pulse's gnrid[]=... to gnrid=... (scalar) without firing hashchange. AngularJS's $location
  // would never see gnrid, fooling our chart detection into skipping the filter navigation.
  // Only tighten (normalize bracket params) for non-deep-link page states.
  if (!hasRegFlowDeepLinkPending()) {
    tightenRegFlowChartHashInLocationBar({ dataPage: props.dataPage, refreshPulse: false })
  }
  startDataPageObserver()
}

const startDataPageObserver = () => {
  if (!import.meta.client) return

  const container = document.getElementById('www-spa-container')
  if (!container) return

  if (dataPageObserver) {
    dataPageObserver.disconnect()
    dataPageObserver = null
  }

  dataPageObserver = new MutationObserver(() => {
    const current = container.getAttribute('data-page') || ''
    const expected = normalizeAvailChartsLockRoute(
      props.dataPage.startsWith('/') ? props.dataPage : `/${props.dataPage}`,
    )
    const normalizedCurrent = normalizeAvailChartsLockRoute(
      current.startsWith('/') ? current : `/${current}`,
    )
    if (normalizedCurrent !== expected) {
      applyChartDataPage(true)
    }
  })

  dataPageObserver.observe(container, {
    attributes: true,
    attributeFilter: ['data-page'],
  })
}

watch(
  () => props.dataPage,
  () => {
    applyChartDataPage(true)
  },
)

onMounted(() => {
  applyChartDataPage(hasRegFlowDeepLinkPending())
  startDataPageObserver()
  prefetchPulseRoute(props.dataPage)
  startPulseChartMonitoring(chartContainerRef, { routePath: props.dataPage })
})

onBeforeUnmount(() => {
  dataPageObserver?.disconnect()
  dataPageObserver = null
})
</script>
<template>
  <div :class="{ 'show-promo-banner': showPromoBanner }" class="www-pulseapp position-relative full-avc d-none d-md-block" :data-page="dataPage" id="www-spa-container" >
    <ClientOnly>
      <div ref="chartContainerRef" class="pulseapp">
        <AngularSpinner />    
        <div v-if="showPulseChartError" class="pulse-error-message">
          Sorry, something went wrong. Please refresh to try again!
        </div>
        <div class="shell-container" :ng-include="`'${zigzagUrl}'`">
          <div class="box-wrapper">
            
          </div>
        </div>
      </div>      
    </ClientOnly>
  </div>
</template>

<style lang="scss">
#location-page-components {
  idp-avc-availability-chart .av-sessions-header, idp-avc-availability-chart-virtual .av-sessions-header.align-items-stretch {
    top: 138px !important;
  }

  .show-promo-banner {
    idp-avc-availability-chart .av-sessions-header, idp-avc-availability-chart-virtual .av-sessions-header.align-items-stretch {
      top: 175px !important;
    }
  }

  idp-avc-availability-chart idp-avc-session-cell {
    background-color: white;
  }

  .full-avc {
    min-height: 300px;
    background-color: #d9d9d9;
    .pulse-error-message {
      margin: 1rem auto;
      padding: 0.75rem 1rem;
      max-width: 500px;
      text-align: center;
      border-radius: 4px;
      background: #ffffff;
      color: #d32f2f;
      font-weight: 600;
    }
    #global_spinner {
      position: relative !important;
      #spinner-message {
        top: 150px;
      }
    }
  }
} 
</style>
